// Audio playback for ParleNorvégien.
//
// Strategy (per call):
//   1. If `audioUrl(s)` passed → play the audio file (HTMLAudioElement).
//      Multiple URLs = rotate across calls for HVPT variability.
//   2. Else fall back to SpeechSynthesis with the best available nb-NO voice.
//      Preference order: Edge neural ("Natural") > native nb-NO > nb > no > system default.
//
// What we DON'T do anymore:
//   - Fake siddis by pitch-shifting bokmål. Siddis audio doesn't exist; the
//     UI must label any siddis playback as "lecture en bokmål".
//   - Vary rate/pitch randomly. HVPT variability now comes from real audio
//     URLs when available, and from voice cycling otherwise.

export type SupportedLang = 'nb-NO' | 'no-NO' | 'nb' | 'no';
const LANG_PRIORITY: SupportedLang[] = ['nb-NO', 'no-NO', 'nb', 'no'];

let cachedVoices: SpeechSynthesisVoice[] | null = null;

export function voicesReady(): Promise<SpeechSynthesisVoice[]> {
  if (cachedVoices && cachedVoices.length > 0) return Promise.resolve(cachedVoices);
  if (!('speechSynthesis' in window)) return Promise.resolve([]);
  return new Promise((resolve) => {
    const tryRead = (): boolean => {
      const all = speechSynthesis.getVoices();
      if (all.length > 0) { cachedVoices = all; resolve(all); return true; }
      return false;
    };
    if (tryRead()) return;
    const handler = (): void => {
      if (tryRead()) speechSynthesis.removeEventListener('voiceschanged', handler);
    };
    speechSynthesis.addEventListener('voiceschanged', handler);
    setTimeout(() => { if (!cachedVoices) resolve([]); }, 1500);
  });
}

export async function norwegianVoices(): Promise<SpeechSynthesisVoice[]> {
  const all = await voicesReady();
  const nb: SpeechSynthesisVoice[] = [];
  for (const lang of LANG_PRIORITY) {
    for (const v of all) {
      if (v.lang.toLowerCase().startsWith(lang.toLowerCase()) && !nb.includes(v)) nb.push(v);
    }
  }
  // Neural / "Online (Natural)" voices to the front — better quality.
  return [...nb].sort((a, b) => priorityScore(b) - priorityScore(a));
}

function priorityScore(v: SpeechSynthesisVoice): number {
  let score = 0;
  if (/natural|neural/i.test(v.name)) score += 100;
  if (/online/i.test(v.name)) score += 30;          // Edge cloud voices
  if (v.lang.toLowerCase() === 'nb-no') score += 10;
  if (v.localService) score += 1;                    // small tiebreaker
  return score;
}

// --- TTS playback ---

export interface SpeakOptions {
  voiceIndex?: number;
  rate?: number;
  pitch?: number;
}

export async function speak(text: string, opts: SpeakOptions = {}): Promise<void> {
  if (!('speechSynthesis' in window)) return;
  const voices = await norwegianVoices();
  speechSynthesis.cancel();

  const u = new SpeechSynthesisUtterance(text);
  if (voices.length > 0) {
    u.voice = voices[(opts.voiceIndex ?? 0) % voices.length]!;
    u.lang = u.voice.lang;
  } else {
    u.lang = 'nb-NO';
  }
  if (opts.rate !== undefined) u.rate = opts.rate;
  if (opts.pitch !== undefined) u.pitch = opts.pitch;

  await new Promise<void>((resolve) => {
    u.onend = () => resolve();
    u.onerror = () => resolve();
    speechSynthesis.speak(u);
  });
}

export function cancelSpeech(): void {
  if ('speechSynthesis' in window) speechSynthesis.cancel();
  if (currentAudio) { currentAudio.pause(); currentAudio = null; }
}

// --- Audio-file playback ---

let currentAudio: HTMLAudioElement | null = null;
const probedAudio = new Map<string, boolean>();  // URL → reachable

/** Play a remote audio file. Resolves on `ended` (or `error`). */
export async function playAudio(url: string): Promise<boolean> {
  if (currentAudio) currentAudio.pause();
  const audio = new Audio(url);
  audio.crossOrigin = 'anonymous';
  audio.preload = 'auto';
  currentAudio = audio;
  return new Promise<boolean>((resolve) => {
    let settled = false;
    const done = (ok: boolean): void => {
      if (settled) return;
      settled = true;
      probedAudio.set(url, ok);
      if (currentAudio === audio) currentAudio = null;
      resolve(ok);
    };
    audio.addEventListener('ended', () => done(true), { once: true });
    audio.addEventListener('error', () => done(false), { once: true });
    audio.addEventListener('abort', () => done(false), { once: true });
    audio.play().catch(() => done(false));
    setTimeout(() => done(false), 10_000);
  });
}

// --- Hybrid play (audio-first, TTS fallback) ---

export interface PlayOptions {
  /** Human-voice audio (NTNU NoW), preferred when available. */
  audio?: string[] | undefined;
  /** Azure Neural TTS audio (Iselin/Pernille/Finn), preferred over live Web Speech. */
  ttsAudio?: string[] | undefined;
  /** Index used to pick which audio URL / which voice. */
  variant?: number;
  rate?: number;
  pitch?: number;
}

export type PlaySource = 'human' | 'neural' | 'tts' | 'none';

/**
 * Play a word/sentence with a 3-tier preference:
 *   1. Human voice (NTNU NoW)
 *   2. Azure Neural TTS (pre-generated)
 *   3. Live Web Speech (system voice, may be robotic)
 * Returns which source actually played, so the UI can show provenance.
 */
export async function playWord(text: string, opts: PlayOptions = {}): Promise<PlaySource> {
  const variant = opts.variant ?? 0;

  if (opts.audio && opts.audio.length > 0) {
    const url = opts.audio[variant % opts.audio.length]!;
    if (await playAudio(url)) return 'human';
  }

  if (opts.ttsAudio && opts.ttsAudio.length > 0) {
    const url = opts.ttsAudio[variant % opts.ttsAudio.length]!;
    if (await playAudio(url)) return 'neural';
  }

  const ttsOpts: SpeakOptions = { voiceIndex: variant };
  if (opts.rate !== undefined) ttsOpts.rate = opts.rate;
  if (opts.pitch !== undefined) ttsOpts.pitch = opts.pitch;
  await speak(text, ttsOpts);
  return ('speechSynthesis' in window) ? 'tts' : 'none';
}

// --- Capability probe (Paramètres) ---

export interface SpeechCapabilities {
  hasSpeech: boolean;
  voiceCount: number;
  voiceNames: string[];
  /** Names containing "Natural" or "Neural" — high-quality cloud voices. */
  neuralVoices: string[];
}

export async function speechCapabilities(): Promise<SpeechCapabilities> {
  const voices = await norwegianVoices();
  const neural = voices.filter((v) => /natural|neural/i.test(v.name)).map((v) => v.name);
  return {
    hasSpeech: 'speechSynthesis' in window,
    voiceCount: voices.length,
    voiceNames: voices.map((v) => v.name),
    neuralVoices: neural,
  };
}
